import {
  DeleteOutlined,
  PlusOutlined,
  ReloadOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import {
  Alert,
  App,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React, { useState } from 'react';
import { fetchPlans } from '@/services/plan/api';
import {
  fetchWooCommerceOrderMapping,
  saveWooCommerceOrderMapping,
} from '@/services/woocommerce-order-mapping/api';

type Values = API.WooCommerceOrderMappingSaveParams;
type Field = { name: number; key: number };
const PERIODS: Record<string, string> = {
  weekly: '周付',
  monthly: '月付',
  quarterly: '季付',
  half_yearly: '半年付',
  yearly: '年付',
  two_yearly: '两年付',
  three_yearly: '三年付',
  onetime: '一次性',
  reset_traffic: '重置流量',
};

/** Maintain authenticated payment sources and exact slug-to-entitlement mappings. */
const SystemConfigEntry: React.FC = () => {
  const { message } = App.useApp();
  const [form] = Form.useForm<Values>();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadFailed, setLoadFailed] = useState(true);
  const [plans, setPlans] = useState<API.PlanItem[]>([]);
  const [periods, setPeriods] = useState<string[]>([]);
  const sources: API.WooCommercePaymentSource[] =
    Form.useWatch('sources', form) ?? [];

  const load = async () => {
    setLoading(true);
    setLoadFailed(true);
    try {
      const [mappingRes, planRes] = await Promise.all([
        fetchWooCommerceOrderMapping(),
        fetchPlans(),
      ]);
      const data = (mappingRes as any)?.data?.data ?? mappingRes.data;
      if (
        mappingRes.code !== 0 ||
        planRes.code !== 0 ||
        !Array.isArray(planRes.data) ||
        !Array.isArray(data?.sources) ||
        !Array.isArray(data?.mappings) ||
        !Array.isArray(data?.periods)
      ) {
        throw new Error('配置或套餐列表加载失败');
      }
      form.setFieldsValue({ sources: data.sources, mappings: data.mappings });
      setPlans(planRes.data);
      setPeriods(data.periods);
      setLoadFailed(false);
    } catch {
      message.error('配置或套餐列表加载失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (loadFailed || loading || saving) return;
    let values: Values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const keys = values.mappings.map((item) =>
      JSON.stringify([
        item.site_key,
        item.product_slug,
        item.purchase_type,
        item.purchase_type === 'one_time' ? '' : item.billing_period,
        item.purchase_type === 'one_time' ? 0 : Number(item.billing_interval),
      ]),
    );
    if (new Set(keys).size !== keys.length) {
      message.error('商品映射组合重复');
      return;
    }
    setSaving(true);
    try {
      const res = await saveWooCommerceOrderMapping({
        sources: values.sources,
        mappings: values.mappings.map((item) => ({
          site_key: item.site_key,
          product_slug: item.product_slug,
          purchase_type: item.purchase_type,
          billing_period:
            item.purchase_type === 'one_time' ? '' : item.billing_period,
          billing_interval:
            item.purchase_type === 'one_time'
              ? 0
              : Number(item.billing_interval),
          plan_id: Number(item.plan_id),
          period: item.period,
          enabled: item.enabled,
        })),
      });
      if (res.code !== 0) {
        message.error(res.msg || '保存失败');
        return;
      }
      message.success('订单映射配置已保存');
      await load();
    } catch {
      message.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  const textCell = (
    field: Field,
    key: string,
    placeholder?: string,
    pattern?: RegExp,
  ) => (
    <Form.Item
      name={[field.name, key]}
      rules={[
        { required: true },
        ...(pattern ? [{ pattern, message: '格式不正确' }] : []),
      ]}
    >
      <Input placeholder={placeholder} />
    </Form.Item>
  );
  const enabledCell = (field: Field) => (
    <Form.Item name={[field.name, 'enabled']} valuePropName="checked">
      <Switch />
    </Form.Item>
  );
  const deleteCell = (field: Field, remove: (index: number) => void) => (
    <Tooltip title="删除">
      <Button
        danger
        type="text"
        aria-label="删除"
        icon={<DeleteOutlined />}
        onClick={() => remove(field.name)}
      />
    </Tooltip>
  );

  return (
    <>
      <Tooltip title="系统配置">
        <Button
          type="text"
          aria-label="系统配置"
          icon={<SettingOutlined />}
          onClick={() => {
            setOpen(true);
            void load();
          }}
        />
      </Tooltip>
      <Modal
        open={open}
        title="系统配置 / 订单映射配置"
        width="92vw"
        onCancel={() => setOpen(false)}
        footer={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              disabled={loading || saving}
              onClick={() => void load()}
            >
              刷新
            </Button>
            <Button
              type="primary"
              loading={saving}
              disabled={loadFailed || loading}
              onClick={() => void save()}
            >
              保存配置
            </Button>
          </Space>
        }
      >
        {loadFailed && !loading ? (
          <Alert
            type="error"
            showIcon
            message="配置未加载成功，禁止保存"
            style={{ marginBottom: 16 }}
          />
        ) : null}
        <Form
          form={form}
          layout="vertical"
          initialValues={{ sources: [], mappings: [] }}
          disabled={loading || saving || loadFailed}
        >
          <Form.List name="sources">
            {(fields, { add, remove }) => {
              const columns: ColumnsType<Field> = [
                {
                  title: '来源标识',
                  width: 180,
                  render: (_, field) =>
                    textCell(
                      field,
                      'site_key',
                      'buy-main',
                      /^[a-z0-9][a-z0-9_-]{0,63}$/,
                    ),
                },
                {
                  title: 'HTTPS 站点',
                  width: 300,
                  render: (_, field) =>
                    textCell(
                      field,
                      'site_url',
                      'https://buy.example.com',
                      /^https:\/\/[^/?#]+\/?$/,
                    ),
                },
                {
                  title: '应用 App ID',
                  width: 240,
                  render: (_, field) => textCell(field, 'app_id'),
                },
                {
                  title: '启用',
                  width: 80,
                  render: (_, field) => enabledCell(field),
                },
                {
                  title: '操作',
                  width: 70,
                  render: (_, field) => deleteCell(field, remove),
                },
              ];
              return (
                <section style={{ marginBottom: 24 }}>
                  <Space style={{ marginBottom: 12 }}>
                    <Typography.Title level={5} style={{ margin: 0 }}>
                      支付来源白名单
                    </Typography.Title>
                    <Button
                      icon={<PlusOutlined />}
                      onClick={() => add({ enabled: true })}
                    >
                      新增来源
                    </Button>
                  </Space>
                  <Table
                    rowKey="key"
                    loading={loading}
                    dataSource={fields}
                    columns={columns}
                    pagination={false}
                    scroll={{ x: 900 }}
                  />
                </section>
              );
            }}
          </Form.List>
          <Form.List name="mappings">
            {(fields, { add, remove }) => {
              const columns: ColumnsType<Field> = [
                {
                  title: '支付来源',
                  width: 160,
                  render: (_, field) => (
                    <Form.Item
                      name={[field.name, 'site_key']}
                      rules={[{ required: true }]}
                    >
                      <Select
                        options={sources
                          .filter((s) => s.site_key)
                          .map((s) => ({
                            label: s.site_key,
                            value: s.site_key,
                          }))}
                      />
                    </Form.Item>
                  ),
                },
                {
                  title: '商品 slug',
                  width: 260,
                  render: (_, field) =>
                    textCell(
                      field,
                      'product_slug',
                      'bbvpnbox-vip-month',
                      /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
                    ),
                },
                {
                  title: '购买方式',
                  width: 130,
                  render: (_, field) => (
                    <Form.Item
                      name={[field.name, 'purchase_type']}
                      rules={[{ required: true }]}
                    >
                      <Select
                        options={[
                          { label: '单购', value: 'one_time' },
                          { label: '订阅', value: 'subscription' },
                        ]}
                        onChange={(value) => {
                          form.setFieldValue(
                            ['mappings', field.name, 'billing_period'],
                            value === 'one_time' ? '' : 'month',
                          );
                          form.setFieldValue(
                            ['mappings', field.name, 'billing_interval'],
                            value === 'one_time' ? 0 : 1,
                          );
                        }}
                      />
                    </Form.Item>
                  ),
                },
                {
                  title: '扣款账期',
                  width: 210,
                  render: (_, field) => (
                    <Form.Item noStyle shouldUpdate>
                      {() => {
                        const single =
                          form.getFieldValue([
                            'mappings',
                            field.name,
                            'purchase_type',
                          ]) === 'one_time';
                        return (
                          <Space align="start">
                            <Form.Item
                              name={[field.name, 'billing_interval']}
                              rules={[{ required: true }]}
                            >
                              <InputNumber
                                disabled={single}
                                min={single ? 0 : 1}
                                max={36}
                                precision={0}
                                style={{ width: 70 }}
                              />
                            </Form.Item>
                            <Form.Item
                              name={[field.name, 'billing_period']}
                              rules={[{ required: !single }]}
                            >
                              <Select
                                disabled={single}
                                style={{ width: 90 }}
                                options={[
                                  { label: '无', value: '' },
                                  { label: '周', value: 'week' },
                                  { label: '月', value: 'month' },
                                  { label: '年', value: 'year' },
                                ]}
                              />
                            </Form.Item>
                          </Space>
                        );
                      }}
                    </Form.Item>
                  ),
                },
                {
                  title: '本地套餐',
                  width: 220,
                  render: (_, field) => (
                    <Form.Item
                      name={[field.name, 'plan_id']}
                      rules={[{ required: true }]}
                    >
                      <Select
                        showSearch
                        optionFilterProp="label"
                        options={plans.map((plan) => ({
                          label: `${plan.name} (#${plan.id})`,
                          value: plan.id,
                        }))}
                      />
                    </Form.Item>
                  ),
                },
                {
                  title: '权益周期',
                  width: 160,
                  render: (_, field) => (
                    <Form.Item
                      name={[field.name, 'period']}
                      rules={[{ required: true }]}
                    >
                      <Select
                        options={periods.map((period) => ({
                          label: PERIODS[period] || period,
                          value: period,
                        }))}
                      />
                    </Form.Item>
                  ),
                },
                {
                  title: '启用',
                  width: 80,
                  render: (_, field) => enabledCell(field),
                },
                {
                  title: '操作',
                  width: 70,
                  render: (_, field) => deleteCell(field, remove),
                },
              ];
              return (
                <section>
                  <Space style={{ marginBottom: 12 }}>
                    <Typography.Title level={5} style={{ margin: 0 }}>
                      商品映射
                    </Typography.Title>
                    <Button
                      icon={<PlusOutlined />}
                      onClick={() =>
                        add({
                          site_key: sources[0]?.site_key,
                          purchase_type: 'one_time',
                          billing_period: '',
                          billing_interval: 0,
                          enabled: true,
                          period: periods[0],
                        })
                      }
                    >
                      新增映射
                    </Button>
                  </Space>
                  <Table
                    rowKey="key"
                    loading={loading}
                    dataSource={fields}
                    columns={columns}
                    pagination={false}
                    scroll={{ x: 1500 }}
                  />
                </section>
              );
            }}
          </Form.List>
        </Form>
      </Modal>
    </>
  );
};

export default SystemConfigEntry;
